import { History, Edit2, Sparkles } from "lucide-react";
import { lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { type Subscription } from "@/types/subscription";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import { useLifecycleEvents } from "@/hooks/useLifecycleEvents";
import { useSubscriptionPeriods } from "@/hooks/useSubscriptionPeriods";
import { useUserSettingsContext } from "@/contexts/UserSettingsContext";
import { useCategories } from "@/hooks/useCategories";
import { generateOptimizationPrompt } from "@/lib/optimizationPromptHelper";
import { toast } from "sonner";

const DeleteConfirmationDialog = lazy(() =>
  import("@/components/DeleteConfirmationDialog").then((module) => ({
    default: module.DeleteConfirmationDialog,
  })),
);

const InfoTooltip = lazy(() =>
  import("@/components/InfoTooltip").then((module) => ({
    default: module.InfoTooltip,
  })),
);

interface ActionButtonsProps {
  subscription: Subscription;
  onEdit?: () => void;
  onDelete: () => void;
  onShowPaymentHistory: () => void;
  paymentHistoryCount: number;
  currentPeriod?: { month: number; year: number };
}

export const ActionButtons = ({
  subscription,
  onEdit,
  onDelete,
  onShowPaymentHistory,
  paymentHistoryCount,
  currentPeriod,
}: ActionButtonsProps) => {
  const { payments } = usePaymentHistory();
  const { lifecycleEvents } = useLifecycleEvents();
  const { subscriptionPeriods } = useSubscriptionPeriods();
  const { displayCurrency } = useUserSettingsContext();
  const { categories } = useCategories();

  const handleGetAISuggestions = async () => {
    try {
      // Filter data for this subscription
      const subPaymentHistory = payments.filter(
        (p) => p.subscription_id === subscription.id
      );
      const subLifecycleEvents = lifecycleEvents.filter(
        (e) => e.subscription_id === subscription.id
      );
      const subPeriods = subscriptionPeriods.filter(
        (p) => p.subscription_id === subscription.id
      );

      // Convert to the format expected by generateOptimizationPrompt
      const convertedPayments = subPaymentHistory.map(p => ({
        id: p.id,
        subscriptionId: p.subscription_id,
        date: new Date(p.payment_date),
        amount: p.amount,
        currency: p.currency,
        status: p.status as any,
        reconciliationSource: p.reconciliation_source as any,
        notes: p.notes,
        createdAt: new Date(p.created_at),
        updatedAt: new Date(p.created_at),
      }));

      const convertedEvents = subLifecycleEvents.map(e => ({
        id: e.id,
        subscriptionId: e.subscription_id,
        event: e.event as any,
        date: new Date(e.event_date),
        reason: e.reason,
        notes: e.notes,
        createdAt: new Date(e.created_at),
      }));

      const convertedPeriods = subPeriods.map(sp => ({
        id: sp.id,
        subscriptionId: sp.subscription_id,
        startDate: new Date(sp.start_date),
        endDate: sp.end_date ? new Date(sp.end_date) : undefined,
        totalPayments: sp.total_payments,
        totalAmount: sp.total_amount,
        averageMonthlyAmount: sp.average_monthly_amount,
        status: sp.status as any,
        createdAt: new Date(sp.created_at),
        updatedAt: new Date(sp.updated_at),
      }));

      const convertedSub = {
        ...subscription,
        paymentHistory: convertedPayments,
        lifecycleEvents: convertedEvents,
        subscriptionPeriods: convertedPeriods,
      };

      const prompt = generateOptimizationPrompt({
        subscription: convertedSub,
        paymentHistory: convertedPayments,
        lifecycleEvents: convertedEvents,
        subscriptionPeriods: convertedPeriods,
        userCurrency: displayCurrency,
        categories: categories,
      });

      await navigator.clipboard.writeText(prompt);
      toast.success("Prompt Copied!", { description: "Paste into ChatGPT, Claude, or any LLM for personalized optimization suggestions." });
    } catch (error) {
      toast.error("Error", { description: "Failed to copy prompt to clipboard" });
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center justify-between">
        <div className="flex space-x-2"></div>

        <div className="flex space-x-1">
          {currentPeriod && (
            <Suspense fallback={<div className="h-8 w-8" />}>
              <InfoTooltip
                subscription={subscription}
                currentPeriod={currentPeriod}
              />
            </Suspense>
          )}

          <Button
            size="sm"
            variant="ghost"
            onClick={handleGetAISuggestions}
            className="text-muted-foreground hover:text-card-foreground"
            title="Get AI Suggestions"
          >
            <Sparkles className="h-3 w-3" />
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={onShowPaymentHistory}
            className="text-muted-foreground hover:text-card-foreground"
            title="Payment History"
          >
            <History className="h-3 w-3" />
            {paymentHistoryCount > 0 && (
              <span className="ml-1 rounded-full bg-primary px-1 text-xs text-primary-foreground">
                {paymentHistoryCount}
              </span>
            )}
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={onEdit}
            className="text-muted-foreground hover:text-card-foreground"
          >
            <Edit2 className="h-3 w-3" />
          </Button>

          <Suspense fallback={<div className="h-8 w-8" />}>
            <DeleteConfirmationDialog
              subscriptionName={subscription.name}
              onDelete={onDelete}
            />
          </Suspense>
        </div>
      </div>
    </TooltipProvider>
  );
};
